import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { ScatterplotLayer } from '@deck.gl/layers';
import type { PickingInfo } from '@deck.gl/core';
import googleMapsService from '../../services/GoogleMaps.service';
import { Sector } from '../../services/lookups.service';
import { formatStatus } from '../../utils/utils';

export interface PinVisibility {
  headOffice: boolean;
  subOffice: boolean;
  union: boolean;
  nonUnion: boolean;
  mixed: boolean;
  noVisits: boolean;
}

type JobsiteCategory = 'union' | 'nonUnion' | 'mixed' | 'noVisits';

export interface MapOptions {
  jobSites: any[];
  mechanicalContractors: any[];
  activeFilters: {
    contractor: string[];
    [key: string]: any;
  };
  onMarkerClick?: (siteId: string) => void;
  focusJobsiteId?: string | null;
  visiblePinTypes?: PinVisibility;
}

export interface SectorObject {
  id: string;
  sector: Sector;
}

interface BasePin {
  position: [number, number]; // [lng, lat] — deck.gl longitude-first
  color: [number, number, number, number]; // RGBA
  infoWindowContent: string;
}

interface JobsitePin extends BasePin {
  siteIds: string[];
}

interface ContractorPin extends BasePin {
  contractorIds: string[];
}

/**
 * Gets the marker color based on jobsite union status (hex, for legend use)
 */
export function getJobsiteMarkerColor(aggregatedUnionStatus?: 'union' | 'non-union' | 'other' | 'clac' | 'merit' | 'mixed' | null): string {
  switch (aggregatedUnionStatus) {
    case 'union':    return '#22C55E';
    case 'non-union': return '#A42823';
    case 'mixed':    return '#F97316';
    default:         return '#6B7280';
  }
}

/**
 * Gets the marker color as an RGBA tuple for deck.gl layers
 */
function getJobsiteMarkerRGBA(aggregatedUnionStatus?: 'union' | 'non-union' | 'other' | 'clac' | 'merit' | 'mixed' | null): [number, number, number, number] {
  switch (aggregatedUnionStatus) {
    case 'union':    return [34, 197, 94, 255];
    case 'non-union': return [164, 40, 35, 255];
    case 'mixed':    return [249, 115, 22, 255];
    default:         return [107, 114, 128, 255];
  }
}

/**
 * Buckets a jobsite's aggregated union status into one of the four legend/checkbox categories
 */
function getJobsiteCategory(aggregatedUnionStatus?: 'union' | 'non-union' | 'other' | 'clac' | 'merit' | 'mixed' | null): JobsiteCategory {
  switch (aggregatedUnionStatus) {
    case 'union':    return 'union';
    case 'non-union': return 'nonUnion';
    case 'mixed':    return 'mixed';
    default:         return 'noVisits';
  }
}

function isValidCoord(lat: number, lng: number): boolean {
  return !isNaN(lat) && !isNaN(lng) &&
    lat >= -90 && lat <= 90 &&
    lng >= -180 && lng <= 180;
}

class MapService {
  private map: any = null;
  private overlay: GoogleMapsOverlay | null = null;
  private currentInfoWindow: any = null;
  private jobsitePinData: JobsitePin[] = [];
  private jobsitePinDataByCategory: Record<JobsiteCategory, JobsitePin[]> = {
    union: [],
    nonUnion: [],
    mixed: [],
    noVisits: [],
  };
  private headOfficePinData: ContractorPin[] = [];
  private subOfficePinData: ContractorPin[] = [];
  private onMarkerClick: ((siteId: string) => void) | undefined;

  async initializeMap(mapContainerId: string, _options: MapOptions): Promise<boolean> {
    try {
      const google = await googleMapsService.loadMapsApi();

      const mapContainer = document.getElementById(mapContainerId);
      if (!mapContainer) {
        console.error('Map container not found!');
        return false;
      }

      this.map = new google.maps.Map(mapContainer, {
        center: { lat: 56.1304, lng: -106.3468 },
        zoom: 4,
        clickableIcons: false,
        styles: [
          { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
          { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
          { featureType: 'transit', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] }
        ]
      });

      return new Promise((resolve) => {
        google.maps.event.addListenerOnce(this.map, 'idle', () => {
          this.currentInfoWindow = new google.maps.InfoWindow({
            pixelOffset: new google.maps.Size(0, -5)
          });
          this.overlay = new GoogleMapsOverlay({ layers: [] });
          this.overlay.setMap(this.map);
          resolve(true);
        });
      });
    } catch (error) {
      console.error('Error initializing map:', error);
      return false;
    }
  }

  /**
   * Center the map on a specific jobsite and open its info window
   */
  async centerOnJobsite(jobsiteId: string): Promise<void> {
    if (!this.map) {
      console.warn('Map not initialized');
      return;
    }

    const pin = this.jobsitePinData.find(p => p.siteIds.includes(jobsiteId));
    if (!pin) return;

    const [lng, lat] = pin.position;
    this.map.setCenter({ lat, lng });
    this.map.setZoom(15);

    if (this.currentInfoWindow) {
      const google = await googleMapsService.loadMapsApi();
      this.currentInfoWindow.setContent(pin.infoWindowContent);
      this.currentInfoWindow.setPosition(new google.maps.LatLng(lat, lng));
      this.currentInfoWindow.open(this.map);
    }
  }

  /**
   * Rebuild pin data from jobsites/contractors and re-render all layers
   */
  async updateMarkers(options: MapOptions): Promise<boolean> {
    if (!this.map) {
      console.error('Map not initialized. Call initializeMap first.');
      return false;
    }

    try {
      const { jobSites, onMarkerClick, focusJobsiteId, mechanicalContractors, visiblePinTypes } = options;
      const google = await googleMapsService.loadMapsApi();

      const mapContainer = document.getElementById('map');
      if (!mapContainer) {
        console.error('Map container no longer exists in DOM, need to reinitialize');
        return false;
      }

      if (this.currentInfoWindow) {
        this.currentInfoWindow.close();
      }

      this.onMarkerClick = onMarkerClick;

      // Reset data arrays
      this.jobsitePinData = [];
      this.jobsitePinDataByCategory = { union: [], nonUnion: [], mixed: [], noVisits: [] };
      this.headOfficePinData = [];
      this.subOfficePinData = [];

      if ((!jobSites || jobSites.length === 0) && (!mechanicalContractors || mechanicalContractors.length === 0)) {
        this.map.setCenter({ lat: 56.1304, lng: -106.3468 });
        this.map.setZoom(4);
        this._updateOverlayLayers(visiblePinTypes);
        return true;
      }

      const bounds = new google.maps.LatLngBounds();
      let hasBounds = false;
      let focusedJobsiteId: string | null = null;

      // Group jobsites by coordinates
      const jobsitesByCoord: Record<string, any[]> = {};
      const headOfficeByCoord: Record<string, any[]> = {};
      const subOfficeByCoord: Record<string, any[]> = {};

      jobSites.forEach(site => {
        if (!site.addresses?.coordinates?.coordinates) return;
        const [lat, lng] = site.addresses.coordinates.coordinates;
        const key = `${Number(lat)},${Number(lng)}`;
        if (!jobsitesByCoord[key]) jobsitesByCoord[key] = [];
        jobsitesByCoord[key].push(site);
      });

      // Group all contractor addresses — visibility handled by layer prop, not by filtering here
      mechanicalContractors.forEach(contractor => {
        if (!contractor.address?.length) return;
        contractor.address.forEach((address: any) => {
          if (!address.coordinates?.coordinates) return;
          const [lat, lng] = address.coordinates.coordinates;
          const key = `${Number(lat)},${Number(lng)}`;

          if (address.addressType === 'head-office') {
            if (!headOfficeByCoord[key]) headOfficeByCoord[key] = [];
            headOfficeByCoord[key].push({ contractor, address });
          } else {
            if (!subOfficeByCoord[key]) subOfficeByCoord[key] = [];
            subOfficeByCoord[key].push({ contractor, address });
          }
        });
      });

      const formatSectors = (sectors: SectorObject[]) => {
        if (!sectors || !Array.isArray(sectors) || sectors.length === 0) return 'N/A';
        return sectors.map(s => s.sector?.name).join(', ');
      };

      // Build jobsite pin data
      Object.entries(jobsitesByCoord).forEach(([coordKey, sitesAtCoord]) => {
        const [lat, lng] = coordKey.split(',').map(Number);
        if (!isValidCoord(lat, lng)) return;

        if (focusJobsiteId && sitesAtCoord.some(s => s.id === focusJobsiteId)) {
          focusedJobsiteId = focusJobsiteId;
        }

        const statuses = sitesAtCoord
          .map(site => site.aggregatedUnionStatus)
          .filter(s => s !== null && s !== undefined);

        let locationUnionStatus: 'union' | 'non-union' | 'other' | 'clac' | 'merit' | 'mixed' | null = null;
        if (statuses.length > 0) {
          const unique = Array.from(new Set(statuses));
          locationUnionStatus = unique.length === 1 ? unique[0] : 'mixed';
        }

        const infoWindowContent = sitesAtCoord.map(site => `
          <div style="padding: 8px; max-width: 220px; font-family: Arial, sans-serif; border-bottom:1px solid #eee;">
            <h3 style="font-size: 14px; font-weight: bold; margin-bottom: 6px; color: #A52A2A;">
              ${site.jobSiteName}
            </h3>
            <p style="margin: 3px 0; color: #333; font-size: 12px;">
              <strong>Status:</strong>
              <span>${formatStatus(site.status) || 'N/A'}</span>
            </p>
            <p style="margin: 3px 0; color: #333; font-size: 12px;">
              <strong>Sector:</strong> ${formatSectors(site.jobDetailsSectors)}
            </p>
            <p style="margin: 3px 0; color: #333; font-size: 12px;">
              <strong>Contact:</strong> ${site.siteContact || 'N/A'}
            </p>
            <p style="margin: 3px 0; color: #333; font-size: 12px;">
              <strong>General Contractor:</strong> ${site.generalContractor?.name || 'N/A'}
            </p>
            <p style="margin: 3px 0; color: #333; font-size: 12px;">
              <strong>Address:</strong>
              ${site.addresses.address1}${site.addresses.address2 ? `, ${site.addresses.address2}` : ''}${site.addresses.city ? `, ${site.addresses.city}` : ''}
            </p>
            <div style="margin-top: 6px; text-align: right;">
              <a href="/jobsite-management/details/${site.id}" style="color: #A52A2A; font-size: 12px; text-decoration: none; font-weight: bold;">
                View Details →
              </a>
            </div>
          </div>
        `).join('');

        const jobsitePin: JobsitePin = {
          position: [lng, lat],
          color: getJobsiteMarkerRGBA(locationUnionStatus),
          infoWindowContent,
          siteIds: sitesAtCoord.map(s => s.id),
        };

        this.jobsitePinData.push(jobsitePin);
        this.jobsitePinDataByCategory[getJobsiteCategory(locationUnionStatus)].push(jobsitePin);

        bounds.extend({ lat, lng });
        hasBounds = true;
      });

      // Build head office pin data
      Object.entries(headOfficeByCoord).forEach(([coordKey, contractorAddresses]) => {
        const [lat, lng] = coordKey.split(',').map(Number);
        if (!isValidCoord(lat, lng)) return;

        const infoWindowContent = contractorAddresses.map(({ contractor, address }) => `
          <div style="padding: 8px; max-width: 220px; font-family: Arial, sans-serif; border-bottom:1px solid #eee;">
            <h3 style="font-size: 14px; font-weight: bold; margin-bottom: 6px; color: #1E88E5;">
              ${contractor.name}
            </h3>
            <p style="margin: 3px 0; color: #333; font-size: 12px;">
              <strong>Type:</strong> Head Office
            </p>
            <p style="margin: 3px 0; color: #333; font-size: 12px;">
              <strong>Address:</strong> ${address?.address || 'N/A'}${address?.city ? `, ${address.city}` : ''}
            </p>
            <div style="margin-top: 6px; text-align: right;">
              <a href="/mechanical-contractor-management/details/${contractor.id}" style="color: #1E88E5; font-size: 12px; text-decoration: none; font-weight: bold;">
                View Details →
              </a>
            </div>
          </div>
        `).join('');

        this.headOfficePinData.push({
          position: [lng, lat],
          color: [30, 136, 229, 255],
          infoWindowContent,
          contractorIds: contractorAddresses.map((c: any) => c.contractor.id),
        });

        bounds.extend({ lat, lng });
        hasBounds = true;
      });

      // Build sub office pin data
      Object.entries(subOfficeByCoord).forEach(([coordKey, contractorAddresses]) => {
        const [lat, lng] = coordKey.split(',').map(Number);
        if (!isValidCoord(lat, lng)) return;

        const infoWindowContent = contractorAddresses.map(({ contractor, address }) => `
          <div style="padding: 8px; max-width: 220px; font-family: Arial, sans-serif; border-bottom:1px solid #eee;">
            <h3 style="font-size: 14px; font-weight: bold; margin-bottom: 6px; color: #952ee9;">
              ${contractor.name}
            </h3>
            <p style="margin: 3px 0; color: #333; font-size: 12px;">
              <strong>Type:</strong> Sub Office
            </p>
            <p style="margin: 3px 0; color: #333; font-size: 12px;">
              <strong>Address:</strong> ${address?.address || 'N/A'}${address?.city ? `, ${address.city}` : ''}
            </p>
            <div style="margin-top: 6px; text-align: right;">
              <a href="/mechanical-contractor-management/details/${contractor.id}" style="color: #952ee9; font-size: 12px; text-decoration: none; font-weight: bold;">
                View Details →
              </a>
            </div>
          </div>
        `).join('');

        this.subOfficePinData.push({
          position: [lng, lat],
          color: [149, 46, 233, 255],
          infoWindowContent,
          contractorIds: contractorAddresses.map((c: any) => c.contractor.id),
        });

        bounds.extend({ lat, lng });
        hasBounds = true;
      });

      this._updateOverlayLayers(visiblePinTypes);

      google.maps.event.trigger(this.map, 'resize');

      const totalPins = this.jobsitePinData.length + this.headOfficePinData.length + this.subOfficePinData.length;
      if (hasBounds && totalPins > 0) {
        this.map.fitBounds(bounds);

        if (totalPins === 1) {
          google.maps.event.addListenerOnce(this.map, 'bounds_changed', () => {
            if (this.map.getZoom() > 15) this.map.setZoom(15);
          });
        }
      } else {
        this.map.setCenter({ lat: 56.1304, lng: -106.3468 });
        this.map.setZoom(4);
      }

      if (focusedJobsiteId) {
        this.centerOnJobsite(focusedJobsiteId);
      }

      return true;
    } catch (error) {
      console.error('Error updating markers:', error);
      return false;
    }
  }

  /**
   * Toggle layer visibility without rebuilding pin data
   */
  updateLayerVisibility(pinVisibility: PinVisibility): void {
    this._updateOverlayLayers(pinVisibility);
  }

  private _makeJobsiteLayer(id: string, category: JobsiteCategory, visible: boolean): ScatterplotLayer<JobsitePin> {
    return new ScatterplotLayer<JobsitePin>({
      id,
      data: this.jobsitePinDataByCategory[category],
      visible,
      pickable: true,
      stroked: true,
      filled: true,
      radiusMinPixels: 7,
      radiusMaxPixels: 7,
      lineWidthMinPixels: 1.5,
      getPosition: (d: JobsitePin) => d.position,
      getFillColor: (d: JobsitePin) => d.color,
      getLineColor: [255, 255, 255, 255],
      onClick: (info: PickingInfo) => this._handlePinClick(info),
      onHover: (info: PickingInfo) => this._handlePinHover(info),
    });
  }

  private _updateOverlayLayers(pinVisibility?: PinVisibility): void {
    if (!this.overlay) return;

    this.overlay.setProps({
      layers: [
        this._makeJobsiteLayer('jobsites-union', 'union', pinVisibility?.union ?? true),
        this._makeJobsiteLayer('jobsites-non-union', 'nonUnion', pinVisibility?.nonUnion ?? true),
        this._makeJobsiteLayer('jobsites-mixed', 'mixed', pinVisibility?.mixed ?? true),
        this._makeJobsiteLayer('jobsites-no-visits', 'noVisits', pinVisibility?.noVisits ?? true),
        new ScatterplotLayer<ContractorPin>({
          id: 'head-offices',
          data: this.headOfficePinData,
          visible: pinVisibility?.headOffice ?? false,
          pickable: true,
          stroked: true,
          filled: true,
          radiusMinPixels: 7,
          radiusMaxPixels: 7,
          lineWidthMinPixels: 1.5,
          getPosition: (d: ContractorPin) => d.position,
          getFillColor: (d: ContractorPin) => d.color,
          getLineColor: [255, 255, 255, 255],
          onClick: (info: PickingInfo) => this._handlePinClick(info),
          onHover: (info: PickingInfo) => this._handlePinHover(info),
        }),
        new ScatterplotLayer<ContractorPin>({
          id: 'sub-offices',
          data: this.subOfficePinData,
          visible: pinVisibility?.subOffice ?? true,
          pickable: true,
          stroked: true,
          filled: true,
          radiusMinPixels: 7,
          radiusMaxPixels: 7,
          lineWidthMinPixels: 1.5,
          getPosition: (d: ContractorPin) => d.position,
          getFillColor: (d: ContractorPin) => d.color,
          getLineColor: [255, 255, 255, 255],
          onClick: (info: PickingInfo) => this._handlePinClick(info),
          onHover: (info: PickingInfo) => this._handlePinHover(info),
        }),
      ]
    });
  }

  private _handlePinHover(info: PickingInfo): void {
    const container = this.map?.getDiv();
    if (!container) return;
    container.classList.toggle('pin-hover', !!info.object);
  }

  private _handlePinClick(info: PickingInfo): boolean {
    if (!info.object || !this.currentInfoWindow || !this.map) return false;

    const pin = info.object as BasePin;
    const [lng, lat] = pin.position;

    this.currentInfoWindow.setContent(pin.infoWindowContent);
    this.currentInfoWindow.setPosition(new window.google.maps.LatLng(lat, lng));
    this.currentInfoWindow.open(this.map);

    const jobsitePin = pin as JobsitePin;
    if (jobsitePin.siteIds?.length && this.onMarkerClick) {
      this.onMarkerClick(jobsitePin.siteIds[0]);
    }

    return true;
  }

  cleanup(): void {
    if (this.currentInfoWindow) {
      try { this.currentInfoWindow.close(); } catch (err) { }
      this.currentInfoWindow = null;
    }

    if (this.overlay) {
      try { this.overlay.finalize(); } catch (err) { }
      this.overlay = null;
    }

    this.jobsitePinData = [];
    this.jobsitePinDataByCategory = { union: [], nonUnion: [], mixed: [], noVisits: [] };
    this.headOfficePinData = [];
    this.subOfficePinData = [];

    if (this.map) {
      try {
        if (window.google?.maps) {
          window.google.maps.event.clearInstanceListeners(this.map);
        }
      } catch (err) {
        console.error('Error clearing map listeners:', err);
      }
      this.map = null;
    }
  }
}

export default new MapService();
